/**
 * Tecnologia da Informação, Fase 2 (ADR 0037): gestão técnica dos equipamentos de TI sobre o
 * patrimônio compartilhado (ADR 0031). O bem é a fonte oficial; aqui ficam as regras da ficha
 * técnica (condicional ao tipo do equipamento), dos identificadores, dos reparos, do checklist de
 * devolução e da importação de dados técnicos.
 */
import { z } from 'zod';

/** Tipo técnico do equipamento, deduzido da classe do patrimônio. */
export const IT_DEVICE_KINDS = [
  'computer',
  'mobile',
  'monitor',
  'printer',
  'network',
  'peripheral',
] as const;
export type ItDeviceKind = (typeof IT_DEVICE_KINDS)[number];

/** Classes iniciais de TI do patrimônio (ADR 0031) e o tipo técnico de cada uma. */
const CLASS_KIND: Record<string, ItDeviceKind> = {
  'TI-NOT': 'computer',
  'TI-DES': 'computer',
  'TI-CEL': 'mobile',
  'TI-MON': 'monitor',
  'TI-IMP': 'printer',
  'TI-RED': 'network',
  'TI-PER': 'peripheral',
};

/**
 * Tipo técnico pela classe do bem: o código da própria classe ou o da classe-mãe mais próxima que
 * seja conhecida (classes criadas depois herdam o tipo). Sem correspondência, `peripheral`.
 */
export function itDeviceKindOf(classCodes: readonly (string | null | undefined)[]): ItDeviceKind {
  for (const code of classCodes) {
    if (!code) continue;
    const known = CLASS_KIND[code] ?? CLASS_KIND[code.split(/[._]/)[0] ?? ''];
    if (known) return known;
  }
  return 'peripheral';
}

/** Campos da ficha técnica e os tipos em que cada um se aplica. */
export const IT_DEVICE_FIELDS = {
  hostname: ['computer', 'printer', 'network'],
  networkDomain: ['computer', 'printer', 'network'],
  osFamily: ['computer', 'mobile', 'network'],
  osVersion: ['computer', 'mobile', 'network'],
  cpu: ['computer'],
  ramGb: ['computer', 'mobile'],
  storageGb: ['computer', 'mobile'],
  storageType: ['computer'],
  gpu: ['computer'],
  screenInches: ['computer', 'mobile', 'monitor'],
  resolution: ['monitor'],
  connections: ['monitor', 'computer', 'network', 'printer'],
  firmware: ['printer', 'network'],
  encryption: ['computer', 'mobile'],
  managedBy: ['computer', 'mobile'],
  usbStorage: ['computer'],
  ipMode: ['computer', 'printer', 'network'],
  ipAddress: ['computer', 'printer', 'network'],
} as const satisfies Record<string, readonly ItDeviceKind[]>;
export type ItDeviceField = keyof typeof IT_DEVICE_FIELDS;

export function itFieldApplies(field: ItDeviceField, kind: ItDeviceKind): boolean {
  return (IT_DEVICE_FIELDS[field] as readonly string[]).includes(kind);
}

export const IT_OS_FAMILIES = [
  'windows',
  'macos',
  'linux',
  'ios',
  'android',
  'chromeos',
  'other',
] as const;
export const IT_STORAGE_TYPES = ['ssd', 'nvme', 'hdd', 'emmc', 'other'] as const;
export const IT_CONNECTIONS = [
  'hdmi',
  'displayport',
  'usb_c',
  'vga',
  'dvi',
  'thunderbolt',
  'ethernet',
  'wifi',
  'bluetooth',
  'usb',
] as const;
export const IT_ENCRYPTIONS = [
  'bitlocker',
  'filevault',
  'luks',
  'device',
  'none',
  'unknown',
] as const;
export const IT_MANAGED_BY = ['none', 'intune', 'jamf', 'google', 'other'] as const;
/** Armazenamento USB no equipamento (I2, 0344). */
export const IT_USB_STORAGE = ['blocked', 'allowed', 'unknown'] as const;
export const IT_IDENTIFIER_KINDS = [
  'mac_ethernet',
  'mac_wifi',
  'imei',
  'serial_technical',
  'other',
] as const;
export type ItIdentifierKind = (typeof IT_IDENTIFIER_KINDS)[number];
export const IT_HOSTNAME_POLICIES = ['block', 'warn'] as const;
export const IT_REPAIR_KINDS = ['corrective', 'warranty', 'upgrade', 'preventive'] as const;
/** Modalidade do reparo (B0.5): pela equipe da TI ou por um prestador. */
export const IT_REPAIR_EXECUTIONS = ['internal', 'external'] as const;
/** Tipos de item de custo do reparo (B0.5). */
export const IT_REPAIR_COST_KINDS = [
  'part',
  'labor',
  'provider_service',
  'freight',
  'other',
] as const;
export const IT_REPAIR_STATUSES = [
  'open',
  'in_service',
  'returned',
  'closed',
  'cancelled',
] as const;
export const IT_OFFBOARDING_REASONS = ['termination', 'transfer', 'leave', 'other'] as const;
export const IT_OFFBOARDING_ITEM_STATUSES = [
  'pending',
  'returned',
  'recovered',
  'revoked',
  'waived',
] as const;
/** Tipos de item do checklist: equipamento, licença e, no desligamento, os acessos (0292). */
export const IT_OFFBOARDING_ITEM_KINDS = ['asset', 'license', 'access', 'erp_login'] as const;
export type ItOffboardingItemKind = (typeof IT_OFFBOARDING_ITEM_KINDS)[number];
/** Atividade do SLA corporativo que mede a retirada dos acessos no desligamento (ADR 0061). */
export const IT_OFFBOARDING_SLA_ACTIVITY = 'TI.DESLIGAMENTO.REVOGACAO';
export const IT_OFFBOARDING_SLA_RECORD_TYPE = 'it.offboarding';
/** Reparos recorrentes: esta quantidade ou mais nos últimos 12 meses. */
export const IT_RECURRING_REPAIRS = 2;
/** Situações do patrimônio em que o equipamento está fora de uso (licença deve ser revista). */
export const IT_RETIRED_ASSET_STATUSES = ['disposed', 'missing', 'returned_to_owner'] as const;

// ---------------------------------------------------------------------------
// Identificadores
// ---------------------------------------------------------------------------

/** MAC com 12 dígitos hexadecimais, em qualquer separação, como `AA:BB:CC:DD:EE:FF`; ou null. */
export function normalizeMac(raw: string): string | null {
  const hex = raw.replace(/[^0-9a-fA-F]/g, '').toUpperCase();
  if (hex.length !== 12 || raw.replace(/[0-9a-fA-F:.\-\s]/g, '') !== '') return null;
  return hex.match(/.{2}/g)!.join(':');
}

/** MAC aleatório ou administrado localmente (2º bit do 1º octeto): não serve como identificador. */
export function isLocallyAdministeredMac(mac: string): boolean {
  return (parseInt(mac.slice(0, 2), 16) & 0b10) !== 0;
}

/** IMEI com 15 dígitos e dígito verificador (Luhn); ou null. */
export function normalizeImei(raw: string): string | null {
  const digits = raw.replace(/[\s.-]/g, '');
  if (!/^\d{15}$/.test(digits)) return null;
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    let d = Number(digits[14 - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0 ? digits : null;
}

/** Valor normalizado de um identificador conforme o tipo, ou o motivo da recusa. */
export function normalizeIdentifier(
  kind: ItIdentifierKind,
  raw: string,
): { value: string } | { error: string } {
  const v = raw.trim();
  if (!v) return { error: 'Informe o valor' };
  if (kind === 'mac_ethernet' || kind === 'mac_wifi') {
    const mac = normalizeMac(v);
    return mac ? { value: mac } : { error: 'MAC inválido: use 12 dígitos hexadecimais' };
  }
  if (kind === 'imei') {
    const imei = normalizeImei(v);
    return imei ? { value: imei } : { error: 'IMEI inválido: 15 dígitos com dígito verificador' };
  }
  if (v.length > 120) return { error: 'Até 120 caracteres' };
  return { value: kind === 'serial_technical' ? v.toUpperCase() : v };
}

// ---------------------------------------------------------------------------
// Formulários
// ---------------------------------------------------------------------------

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const id = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const day = z
  .string()
  .regex(/^(\d{4}-\d{2}-\d{2})?$/, 'Data no formato AAAA-MM-DD')
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const choice = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .enum(values)
    .or(z.literal(''))
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const amount = z
  .string()
  .trim()
  // Valor negativo tem mensagem própria (B12); o formato continua com ponto.
  .refine((v) => !v.startsWith('-'), 'O valor não pode ser negativo')
  .refine(
    (v) => v.startsWith('-') || /^\d{1,15}(\.\d{1,4})?$/.test(v),
    'Use números com ponto (ex.: 1500.00)',
  );

/** Ficha técnica. Campos que não se aplicam ao tipo do equipamento são recusados no servidor. */
export const itDeviceInput = z
  .object({
    hostname: z
      .string()
      .trim()
      .regex(
        /^([A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)?$/,
        'Hostname: letras, números e hífen',
      )
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    networkDomain: z
      .string()
      .trim()
      .toLowerCase()
      .regex(/^([a-z0-9.-]{1,253})?$/, 'Domínio de rede inválido')
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    hostnameExceptionReason: text(2000),
    osFamily: choice(IT_OS_FAMILIES),
    osVersion: text(80),
    cpu: text(120),
    ramGb: z.number().positive().max(65536).nullable().optional(),
    storageGb: z.number().int().min(1).max(10_000_000).nullable().optional(),
    storageType: choice(IT_STORAGE_TYPES),
    gpu: text(120),
    screenInches: z.number().positive().max(199).nullable().optional(),
    resolution: z
      .string()
      .trim()
      .regex(/^(\d{3,5}x\d{3,5})?$/, 'Resolução no formato 1920x1080')
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    connections: z
      .array(z.enum(IT_CONNECTIONS))
      .default([])
      .transform((v) => [...new Set(v)]),
    firmware: text(80),
    encryption: choice(IT_ENCRYPTIONS),
    managedBy: z.enum(IT_MANAGED_BY).default('none'),
    usbStorage: choice(IT_USB_STORAGE),
    ipMode: choice(['dhcp', 'static'] as const),
    ipAddress: text(39),
    notes: text(4000),
    note: text(2000),
  })
  .refine((v) => !v.ipAddress || v.ipMode === 'static', {
    path: ['ipAddress'],
    message: 'IP só para endereço fixo',
  })
  .refine((v) => !v.hostnameExceptionReason || !!v.hostname, {
    path: ['hostnameExceptionReason'],
    message: 'A exceção é do hostname: informe o hostname',
  })
  .refine((v) => !v.hostnameExceptionReason || v.hostnameExceptionReason.length >= 3, {
    path: ['hostnameExceptionReason'],
    message: 'Explique a exceção',
  });
export type ItDeviceInput = z.infer<typeof itDeviceInput>;

/** Campos preenchidos que não se aplicam ao tipo do equipamento (para recusar). */
export function itDeviceFieldIssues(input: Partial<ItDeviceInput>, kind: ItDeviceKind) {
  const issues: { path: string; message: string }[] = [];
  for (const field of Object.keys(IT_DEVICE_FIELDS) as ItDeviceField[]) {
    const value = input[field as keyof ItDeviceInput];
    const filled = Array.isArray(value)
      ? value.length > 0
      : field === 'managedBy'
        ? value != null && value !== 'none'
        : value != null;
    if (filled && !itFieldApplies(field, kind)) {
      issues.push({ path: field, message: 'Este campo não se aplica a este tipo de equipamento' });
    }
  }
  return issues;
}

export const itIdentifierInput = z
  .object({
    kind: z.enum(IT_IDENTIFIER_KINDS),
    label: text(60),
    value: z.string().trim().min(1, 'Informe o valor').max(120),
    isTrusted: z.boolean().default(true),
    exceptionReason: text(2000),
  })
  .refine((v) => v.kind !== 'other' || !!v.label, {
    path: ['label'],
    message: 'Dê um nome a este identificador',
  })
  .refine((v) => !v.exceptionReason || v.exceptionReason.length >= 3, {
    path: ['exceptionReason'],
    message: 'Explique a exceção',
  });
export type ItIdentifierInput = z.infer<typeof itIdentifierInput>;

/** Hostname repetido só alerta (Onda 0, D-0.1): "bloquear" não é mais aceito. */
export const itSettingInput = z.object({
  hostnamePolicy: z.literal('warn', { message: 'Hostname repetido só gera alerta' }),
});

export const itRepairInput = z
  .object({
    kind: z.enum(IT_REPAIR_KINDS),
    execution: z.enum(IT_REPAIR_EXECUTIONS),
    /** Reparo interno: o equipamento continua disponível (true) ou entra em manutenção (false). */
    keepsAvailable: z.boolean().nullable().optional(),
    problem: z.string().trim().min(3, 'Descreva o problema').max(4000),
    diagnosis: text(4000),
    solution: text(4000),
    parts: text(2000),
    technicianUserId: id,
    partnerId: id,
    underWarranty: z.boolean().default(false),
    repairWarrantyUntil: day,
    openedOn: day,
  })
  .refine((v) => v.execution === 'external' || !v.partnerId, {
    path: ['partnerId'],
    message: 'Reparo interno não tem prestador',
  })
  .refine((v) => v.execution === 'external' || typeof v.keepsAvailable === 'boolean', {
    path: ['keepsAvailable'],
    message: 'Escolha se o equipamento continua disponível',
  });
export type ItRepairInput = z.infer<typeof itRepairInput>;

/** Item de custo do reparo (B0.5): quantidade, valor unitário, moeda e documento; sem pagamento. */
export const itRepairCostItemInput = z.object({
  kind: z.enum(IT_REPAIR_COST_KINDS),
  description: z.string().trim().min(2, 'Descreva o item').max(300),
  quantity: z
    .string()
    .trim()
    .regex(/^\d{1,9}(\.\d{1,3})?$/, 'Quantidade com até 3 casas (ex.: 1 ou 2.5)')
    .refine((v) => Number(v) > 0, 'A quantidade precisa ser maior que zero'),
  unitAmount: amount,
  currency: z.string().regex(/^[A-Z]{3}$/, 'Moeda com 3 letras (ex.: BRL)'),
  documentRef: text(120),
});
export const itRepairCostItemCancelInput = z.object({
  reason: z.string().trim().min(3, 'Explique o cancelamento').max(2000),
});

export const itRepairCostInput = z.object({
  amount,
  currency: z.string().regex(/^[A-Z]{3}$/, 'Moeda com 3 letras (ex.: BRL)'),
  documentRef: text(120),
});

/** Enviar o equipamento ao serviço (cria a movimentação maintenance_in do patrimônio). */
export const itRepairSendInput = z.object({
  sentOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Informe a data'),
  reason: z.string().trim().min(3, 'Explique o envio').max(2000),
});
/** Retorno do serviço (cria a movimentação maintenance_out do patrimônio). */
export const itRepairReturnInput = z.object({
  returnedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Informe a data'),
  reason: z.string().trim().min(3, 'Explique o retorno').max(2000),
});
/** Pedido da TI ao Patrimônio para enviar o equipamento ao serviço (M1). */
export const itRepairDispatchRequestInput = z.object({
  plannedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Informe a data prevista'),
  note: z.string().trim().min(3, 'Explique o pedido').max(2000),
});
/** Pedido da TI ao Patrimônio para registrar o retorno (opcional: o retorno não exige pedido). */
export const itRepairReturnRequestInput = z.object({
  note: z.string().trim().min(3, 'Explique o pedido').max(2000),
});
/** Ligação de uma movimentação feita pela ficha do bem ao reparo (sem criar outra). */
export const itRepairLinkInput = z.object({
  note: z.string().trim().min(3, 'Explique a ligação').max(2000),
});
export const itRepairCloseInput = z.object({
  closedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Informe a data'),
  solution: z.string().trim().min(3, 'Descreva o resultado').max(4000),
});
export const itRepairCancelInput = z.object({
  reason: z.string().trim().min(3, 'Explique o cancelamento').max(2000),
});

/** Checklist de devolução de um colaborador (com ou sem login, ADR 0085) ou de um login. */
export const itOffboardingInput = z
  .object({
    legalEntityId: z.uuid(),
    employeeId: z.uuid().nullable().optional(),
    userId: z.uuid().nullable().optional(),
    reason: z.enum(IT_OFFBOARDING_REASONS),
    lastDay: day,
    notes: text(4000),
  })
  .refine((v) => !!(v.employeeId || v.userId), {
    path: ['employeeId'],
    message: 'Escolha o colaborador',
  });
export const itOffboardingItemInput = z
  .object({
    status: z.enum(['returned', 'recovered', 'revoked', 'waived'] as const),
    note: text(2000),
  })
  .refine((v) => v.status !== 'waived' || !!v.note, {
    path: ['note'],
    message: 'Explique a dispensa',
  });
export const itOffboardingCloseInput = z.object({ note: text(2000) });
/** Justificativa obrigatória de uma retirada (identificador, anexo, cancelamento de checklist). */
export const itReasonInput = z.object({
  reason: z.string().trim().min(3, 'Explique o motivo').max(1000),
});

// ---------------------------------------------------------------------------
// Admissão: o kit por pessoa (0293)
// ---------------------------------------------------------------------------

/** Atividade do SLA corporativo que mede a preparação da admissão (ADR 0061). */
export const IT_ONBOARDING_SLA_ACTIVITY = 'TI.ADMISSAO.PREPARACAO';
export const IT_ONBOARDING_SLA_RECORD_TYPE = 'it.onboarding';
/** Atividade do SLA corporativo que mede o reparo de equipamento, da abertura ao encerramento. */
export const IT_REPAIR_SLA_ACTIVITY = 'TI.REPARO.EXECUCAO';
export const IT_REPAIR_SLA_RECORD_TYPE = 'it.repair';
export const IT_ONBOARDING_STATUSES = ['open', 'closed', 'cancelled'] as const;
export const IT_ONBOARDING_ITEM_STATUSES = ['pending', 'delivered', 'waived', 'released'] as const;

export const itOnboardingInput = z.object({
  legalEntityId: z.uuid(),
  employeeId: z.uuid(),
  notes: text(4000),
});
/** Item do kit: equipamento (tipo e, se já escolhido, o bem) ou licença (o software). */
export const itOnboardingItemInput = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('device'),
    assetClassId: z.uuid(),
    assetId: z.uuid().nullable().optional(),
  }),
  z.object({ kind: z.literal('license'), softwareId: z.uuid() }),
]);
/** Escolha (ou troca) do bem reservado para o item de equipamento. */
export const itOnboardingReserveInput = z.object({ assetId: z.uuid() });
export const itOnboardingDeliverInput = z.object({ note: text(2000) });
export const itOnboardingWaiveInput = z.object({
  note: z.string().trim().min(1, 'Explique a dispensa').max(2000),
});
export const itOnboardingCloseInput = z.object({ note: text(2000) });
export const itOnboardingCancelInput = z.object({
  note: z.string().trim().min(1, 'Explique o cancelamento').max(2000),
});

// ---------------------------------------------------------------------------
// Importação de dados técnicos (planilha "Equipamentos")
// ---------------------------------------------------------------------------

/** Colunas do modelo da planilha (cabeçalho → campo). */
export const IT_IMPORT_COLUMNS = [
  ['numero_patrimonio', 'tag'],
  ['numero_serie', 'serial'],
  ['hostname', 'hostname'],
  ['dominio_rede', 'networkDomain'],
  ['sistema', 'osFamily'],
  ['versao_sistema', 'osVersion'],
  ['processador', 'cpu'],
  ['memoria_gb', 'ramGb'],
  ['armazenamento_gb', 'storageGb'],
  ['tipo_armazenamento', 'storageType'],
  ['placa_video', 'gpu'],
  ['tela_polegadas', 'screenInches'],
  ['firmware', 'firmware'],
  ['mac_ethernet', 'macEthernet'],
  ['mac_wifi', 'macWifi'],
  ['imei', 'imei'],
] as const;
